/**
 * VFAvatar — avatar circulaire avec initiales, couleur déterministe depuis le nom.
 */
type ColorPair = readonly [string, string];

const PALETTE: ColorPair[] = [
  ['#DBEAFE', '#1E40AF'],
  ['#DCFCE7', '#166534'],
  ['#FFEDD5', '#C2410C'],
  ['#EDE9FE', '#5B21B6'],
  ['#FEE2E2', '#991B1B'],
  ['#FEF3C7', '#92400E'],
  ['#CFFAFE', '#155E75'],
  ['#FCE7F3', '#9D174D'],
];
const DARK_PALETTE: ColorPair[] = [
  ['rgba(30,58,138,0.45)',  '#BFDBFE'],
  ['rgba(20,83,45,0.45)',   '#BBF7D0'],
  ['rgba(146,64,14,0.45)',  '#FED7AA'],
  ['rgba(76,29,149,0.45)',  '#DDD6FE'],
  ['rgba(127,29,29,0.45)',  '#FECACA'],
  ['rgba(120,53,15,0.45)',  '#FDE68A'],
  ['rgba(21,94,117,0.45)',  '#A5F3FC'],
  ['rgba(157,23,77,0.45)',  '#FBCFE8'],
];

function hashCode(s: string = ''): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h);
}

function initials(name: string = ''): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type AvatarStatus = 'online' | 'offline' | 'busy';

const SIZES: Record<AvatarSize, number> = { xs: 20, sm: 24, md: 28, lg: 36, xl: 48 };

interface VFAvatarProps {
  name?: string;
  initials?: string;
  size?: AvatarSize | number;
  status?: AvatarStatus;
  className?: string;
  onClick?: () => void;
  src?: string;
}

export function VFAvatar({
  name = '',
  initials: initialsProp,
  size = 'md',
  status,
  className = '',
  onClick,
  src,
}: VFAvatarProps) {
  const px = typeof size === 'number' ? size : SIZES[size] || 28;
  const letters = (initialsProp ?? initials(name)) || '?';
  // Correction #7 — meme regle que dans le dialer : un rond d'initiales est
  // neutre. La couleur reste reservee aux signaux qui en portent un (statut
  // d'appel, pastille de presence ci-dessous).
  const bg = 'var(--vf-surface-3, #EDEEF1)';
  const fg = 'var(--vf-text-2, #52525B)';

  const STATUS_COLORS: Record<AvatarStatus, string> = {
    online: 'var(--vf-green)',
    busy:   'var(--vf-red)',
    offline:'var(--vf-text-4)',
  };
  const dot = status ? STATUS_COLORS[status] : null;

  return (
    <span
      onClick={onClick}
      className={`relative inline-flex items-center justify-center rounded-full font-semibold ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={{
        width: px, height: px,
        background: src ? 'transparent' : bg,
        color: fg,
        border: src ? 'none' : '1px solid var(--vf-border, #E7E7EA)',
        fontSize: Math.max(9, Math.round(px * 0.38)),
        letterSpacing: '-0.02em',
      }}
      title={name}
    >
      {src
        ? <img src={src} alt={name} className="h-full w-full rounded-full object-cover" />
        : letters}
      {dot && (
        <span
          className="absolute rounded-full border-2"
          style={{
            width: Math.max(8, px * 0.28),
            height: Math.max(8, px * 0.28),
            background: dot,
            borderColor: 'var(--vf-bg)',
            right: -1, bottom: -1,
          }}
        />
      )}
    </span>
  );
}

export default VFAvatar;
